import { useContext, useState } from 'react';
import classnames from 'classnames';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import * as api from '../../../api';
import * as logger from '../../../logger';
import { getFormattedCurrencyAmount } from '../PaymentsPage/utils';

import { Button } from '../../../components/Button';
import { CheckBox } from '../../../components/CheckBox';
import { InfoMessage } from '../../../components/InfoMessage';
import { Page } from '../';
import { PaymentContext } from '../PaymentInfoProvider';
import PaymentLoadingModal from '../Modal/Loading';
import PaymentSelectItem from './PaymentSelectItem';
import PaymentSuccessModal from '../Modal/Success';
import SpinnerButton from '../../../components/SpinnerButton';
import { FormSetSelected } from '../../../reducers/store';
import { getSelectedPracticePreference } from '../../../utils/formSet';

import defaultStyle from '../../../styles/formSetStyles.module.css';
import introPageStyles from '../../FormStart/IntroPage/index.module.css';
import styles from '../index.module.css';

interface Props
  extends RouteComponentProps<{ authenticationCode: string }>,
    StateProps {
  selectedFormSetId: string;
  field: api.PrevisitFormField.PrevisitFormFieldWithConfig;
  navigate: (page: Page) => void;
  onSuccess: (saveValue: string) => Promise<void>;
}

interface StateProps {
  enablePaymentReceipts: boolean;
}

const SelectPaymentPage: React.FC<Props> = ({
  selectedFormSetId,
  field,
  navigate,
  onSuccess,
  match,
  enablePaymentReceipts,
}) => {
  const paymentInfo = useContext(PaymentContext).data;
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [saveReceipt, setSaveReceipt] = useState(enablePaymentReceipts);
  const { t } = useTranslation();

  const selectIndex = (index: number) => {
    if (errorMessage) setErrorMessage(undefined);
    setSelectedIndex(index);
  };

  const handleSelect = async () => {
    setIsSubmitting(true);
    if (selectedIndex === -1) {
      navigate(Page.Add);
      setIsSubmitting(false);
      return;
    }

    try {
      // send a request about card and wait for a response
      await api.client.Payment.finishPaymentWithSavedMethod({
        sendReceipt: false,
        authenticationCode: match.params.authenticationCode,
        methodIndex: selectedIndex,
        previsitFormSetId: selectedFormSetId,
      });
      setShowSuccess(true);
    } catch {
      setErrorMessage(
        t('Error with selected payment method. Please choose another option.'),
      );
    }
    setIsSubmitting(false);
  };

  const handleSuccess = async () => {
    try {
      const saveValue = JSON.stringify({
        ...{ ...JSON.parse(field.value), skipped: undefined },
        selectedIndex,
      });
      await onSuccess(saveValue);
    } catch (err) {
      logger.error(err, 'Ran into issue while submitting success value: ');
      // submit old value to mark completion
      await onSuccess(field.value);
    }
  };

  if (!paymentInfo) return null;

  return (
    <>
      <PaymentLoadingModal isOpen={isSubmitting} />
      <PaymentSuccessModal
        isOpen={showSuccess && !isSubmitting}
        showReceipt={saveReceipt}
        onRequestClose={handleSuccess}
        selectedFormSetId={selectedFormSetId}
      />
      {errorMessage && <InfoMessage isError={true}>{errorMessage}</InfoMessage>}
      <div className={classnames(styles.PaymentSelect)}>
        <div className={introPageStyles.Label}>
          {t('Please choose a payment method below.')}
        </div>
        <div className={classnames(styles.PaymentSelectItemContainer)}>
          {paymentInfo.paymentMethods.map((paymentMethod, index) => (
            <PaymentSelectItem
              key={`psi-${index}`}
              paymentMethod={paymentMethod}
              checked={selectedIndex === index}
              onClick={() => selectIndex(index)}
            />
          ))}
          <PaymentSelectItem
            key="psi-new"
            label={t('Use a different payment method')}
            checked={selectedIndex === -1}
            onClick={() => selectIndex(-1)}
          />
        </div>
        {selectedIndex !== -1 && enablePaymentReceipts && (
          <div className={styles.CheckboxContainer}>
            <CheckBox
              checked={saveReceipt}
              label={t('Email me a receipt')}
              saveFieldValue={() => setSaveReceipt(!saveReceipt)}
              updateFieldValue={() => {}}
            />
          </div>
        )}
        <div className={defaultStyle.ButtonSubmitWrapper}>
          <SpinnerButton
            className={styles.PayButton}
            disabled={isSubmitting}
            type="button"
            showSpinner={isSubmitting}
            onClick={handleSelect}
          >
            {selectedIndex === -1
              ? t('Continue')
              : `${t('Pay total due')}  ${getFormattedCurrencyAmount(
                  paymentInfo.amount || 0,
                  paymentInfo.currency || 'usd',
                )}`}
          </SpinnerButton>
          {!field.required && (
            <Button
              type="button"
              disabled={isSubmitting}
              children={t("I'll pay later")}
              invertColors={true}
              onClick={() => navigate(Page.SkipFeedback)}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default connect((store: FormSetSelected) => ({
  enablePaymentReceipts:
    getSelectedPracticePreference(store).enablePaymentReceipts,
  selectedFormSetId: store.selectedFormSetId,
}))(withRouter(SelectPaymentPage));
